import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { refreshToken } from "../services/keycloakService";
import { jwtRefreshToken } from "../services/jwtAuthService";
import { getEnv } from "../envConfig";

const AUTH_METHOD = getEnv().AUTH_METHOD || "keycloak";
const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const WARN_DURATION = 60;              // 60 second countdown

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];

export const useSessionTimeout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARN_DURATION);

  const idleTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const warningActiveRef = useRef(false);

  // Keep navigate current without making it an effect dependency.
  // This breaks the navigate → functions → useEffect re-run chain
  // that caused racing / duplicate timers in the previous implementation.
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  // Stable refs so the modal buttons always call the latest handler
  // even after React re-renders the component.
  const stayLoggedInRef = useRef(null);
  const logoutNowRef = useRef(null);

  // When an anonymous user navigates away (pagehide) mark it.
  // On return via bfcache (pageshow), end the session so login is required.
  useEffect(() => {
    const isAnonymous = currentUser?.roles?.some(
      (r) => r.toLowerCase() === "anonymous"
    );
    if (!isAnonymous) return;

    const onPageHide = () => {
      sessionStorage.setItem("anonymous_left_app", "true");
    };

    const onPageShow = () => {
      if (!sessionStorage.getItem("anonymous_left_app")) return;
      const navType = performance?.getEntriesByType?.("navigation")?.[0]?.type;
      if (navType === "reload") {
        sessionStorage.removeItem("anonymous_left_app");
        return;
      }
      sessionStorage.removeItem("anonymous_session_active");
      sessionStorage.removeItem("anonymous_left_app");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      dispatch({ type: "LOGOUT" });
      navigateRef.current("/login", { replace: true });
    };

    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [currentUser, dispatch]);

  useEffect(() => {
    // Skip session timeout entirely for anonymous users — they use
    // short-lived guest tokens that are not expected to be kept alive.
    const isAnonymous = currentUser?.roles?.some(
      (r) => r.toLowerCase() === "anonymous"
    );
    if (!currentUser || isAnonymous) return;

    // ── helpers ─────────────────────────────────────────────────────────────
    const clearTimers = () => {
      clearTimeout(idleTimerRef.current);
      clearInterval(countdownTimerRef.current);
      idleTimerRef.current = null;
      countdownTimerRef.current = null;
    };

    // Forward declaration — doLogout references handleActivity which is
    // defined below. Safe because doLogout() is only ever invoked from
    // async callbacks (timers), by which time handleActivity is assigned.
    let handleActivity;

    const doLogout = () => {
      setShowWarning(false);
      setCountdown(WARN_DURATION);
      warningActiveRef.current = false;
      clearTimers();

      // Remove listeners immediately so no stale resetIdleTimer call on
      // the login page can schedule a new timer that would log out a
      // freshly re-authenticated user.
      if (handleActivity) {
        ACTIVITY_EVENTS.forEach((e) =>
          window.removeEventListener(e, handleActivity, true)
        );
      }

      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("anonymous_session_active");
      dispatch({ type: "LOGOUT" });
      navigateRef.current("/login", { replace: true });
    };

    const startCountdown = () => {
      if (warningActiveRef.current) return; // already counting down
      warningActiveRef.current = true;
      setShowWarning(true);
      setCountdown(WARN_DURATION);

      let remaining = WARN_DURATION;
      countdownTimerRef.current = setInterval(() => {
        remaining -= 1;
        setCountdown(remaining);
        if (remaining <= 0) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
          doLogout();
        }
      }, 1000);
    };

    const scheduleIdleTimer = () => {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(startCountdown, IDLE_TIMEOUT);
    };

    handleActivity = () => {
      if (warningActiveRef.current) return;
      scheduleIdleTimer();
    };

    // ── modal button handlers (always call the latest closure) ───────────────
    stayLoggedInRef.current = async () => {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
      warningActiveRef.current = false;
      setShowWarning(false);
      setCountdown(WARN_DURATION);

      try {
        let newTokens;
        if (AUTH_METHOD === "jwt") {
          newTokens = await jwtRefreshToken();
        } else {
          newTokens = await refreshToken();
        }

        // Both services now return { accessToken, refreshToken }.
        // Sync into Redux so AuthGuard and useTokenRefresh always see
        // a non-stale, non-null accessToken.
        if (newTokens?.accessToken) {
          dispatch({
            type: "UPDATE_TOKENS",
            payload: {
              accessToken: newTokens.accessToken,
              refreshToken: newTokens.refreshToken,
            },
          });
        }
      } catch {
        doLogout();
        return;
      }

      scheduleIdleTimer();
    };

    logoutNowRef.current = () => {
      clearInterval(countdownTimerRef.current);
      doLogout();
    };

    // ── start ────────────────────────────────────────────────────────────────
    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, handleActivity, true)
    );
    scheduleIdleTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((e) =>
        window.removeEventListener(e, handleActivity, true)
      );
      clearTimers();
      warningActiveRef.current = false;
      stayLoggedInRef.current = null;
      logoutNowRef.current = null;
    };
  }, [currentUser, dispatch]); // navigate kept fresh via navigateRef — not a dep

  return {
    showWarning,
    countdown,
    stayLoggedIn: () => stayLoggedInRef.current?.(),
    logoutNow: () => logoutNowRef.current?.(),
  };
};

export default useSessionTimeout;
